import * as React from 'react';

import {Button} from 'shells/button/button';
import {CategorizedSelectList} from 'shells/categorized-select-list/categorized-select-list';
import type {Category} from 'shells/categorized-select-list/categorized-select-list';
import {AddNewCard} from 'shells/edition-thumbnail-list/add-new-card';
// @ts-expect-error - imported from .js/.jsx file
import {TemplateIcon} from 'shells/icon/svg/template-icon';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {FooterBar} from 'shells/modal/footer-bar';
import {Modal} from 'shells/modal/modal';
import {ThumbnailButtonCard} from 'shells/thumbnail/thumbnail-button-card/thumbnail-button-card';

import type {Flow} from 'flow-to-typescript-codemod';

import {PageThumbnailButtonCard, THUMBNAIL_HEIGHT} from '../page-card/page-thumbnail-button-card';
import type {Props as PageProps} from '../page-card/page-thumbnail-button-card';

import type {SelectedStarter} from './create-page-modal-root';

export type StarterOption = {
    id: string;
    createNew?: {
        height?: number;
    };
    existingPage?: Flow.Diff<
        PageProps,
        {
            onClick: () => void;
        }
    >;
    templatePage?: {
        name: string;
        thumbnailUrl: string | null | undefined;
        backgroundColor: string;
    };
};

type Props = {
    onClose: () => void;
    onClickNext: () => void;
    starterOptions: Category<StarterOption>[] | null | undefined;
    selectedStarter: SelectedStarter;
    setSelectedStarter: (arg1: SelectedStarter) => void;
    isLoading: boolean;
};

export function StartFromPageSelectionModal(props: Props) {
    return (
        <Modal isOpen={true} isWide={true} onClose={props.onClose} headerText='Create a new page'>
            <div
                style={{
                    // full height minus footer bar height + 16px margin
                    height: 'calc(100% - 99px)',
                }}
                className='mb-16'
            >
                <CategorizedSelectList
                    categorizedLists={props.isLoading ? undefined : props.starterOptions}
                    renderItem={(item, category) =>
                        // @ts-expect-error
                        renderItem(
                            item,
                            () =>
                                props.setSelectedStarter({id: item.id, name: getStarterName(item)}),
                            props.selectedStarter,
                            category.title
                        )
                    }
                    onSelectCategory={(category) => {
                        // select first starter in category
                        if (category.collection) {
                            const starter = category.collection[0];

                            props.setSelectedStarter({
                                // @ts-expect-error
                                id: starter.id,
                                // @ts-expect-error
                                name: getStarterName(starter),
                            });
                        }
                    }}
                    isLoading={props.isLoading}
                />
            </div>
            <FooterBar
                hasBorder={true}
                left={
                    <Button
                        onClick={props.onClose}
                        variant='text-secondary'
                        size='big'
                        noPadding={true}
                    >
                        Cancel
                    </Button>
                }
                right={
                    <Button onClick={props.onClickNext} variant='save' size='big'>
                        {props.selectedStarter.name
                            ? `Start from '${props.selectedStarter.name}'`
                            : 'Start from scratch'}
                    </Button>
                }
            />
        </Modal>
    );
}

const renderItem = (
    item: StarterOption,
    onClick: () => void,
    selectedStarter: SelectedStarter,
    categoryTitle: React.ReactNode
) => {
    if (item.existingPage) {
        return (
            <PageThumbnailButtonCard
                key={item.id}
                name={item.existingPage.name}
                nutshellPublishedUrl={item.existingPage.nutshellPublishedUrl}
                customPublishedUrl={item.existingPage.customPublishedUrl}
                lastModifiedTime={item.existingPage.lastModifiedTime}
                thumbnailUrl={item.existingPage.thumbnailUrl}
                isSelected={selectedStarter.id === item.id}
                onClick={onClick}
                backgroundColor={item.existingPage.backgroundColor}
            />
        );
    } else if (item.createNew) {
        return (
            <AddNewCard
                key={item.id}
                text='Start from scratch'
                isSelectable={true}
                height={item.createNew.height}
                onClick={onClick}
                isSelected={selectedStarter.id === item.id}
            />
        );
    } else if (item.templatePage) {
        return (
            <ThumbnailButtonCard
                key={item.id}
                title={item.templatePage.name}
                thumbnail={item.templatePage.thumbnailUrl}
                thumbnailHeight={THUMBNAIL_HEIGHT}
                secondaryInfo={
                    <StackLegacy spacing={8} isHorizontal={true} alignCenter={true}>
                        <div className='flex'>
                            <TemplateIcon size={13} wrapWithDiv={false} />
                        </div>
                        <div>{categoryTitle}</div>
                    </StackLegacy>
                }
                isSelected={selectedStarter.id === item.id}
                onClick={onClick}
                backgroundColor={item.templatePage.backgroundColor}
            />
        );
    } else {
        return null;
    }
};

const getStarterName = (selectedStarter: StarterOption): string | null | undefined => {
    if (selectedStarter.existingPage) {
        return selectedStarter.existingPage.name;
    } else if (selectedStarter.templatePage) {
        return selectedStarter.templatePage.name;
    } else {
        return null;
    }
};
